"use client";

import React, { useState, useEffect } from "react";
import {
  MessageCircle,
  Instagram,
  Twitter,
  Facebook,
  ShoppingCart,
  Menu,
  X,
} from "lucide-react";
import logo from "../../assets/logo.png";
import Link from "next/link";
import { usePathname } from "next/navigation";

const tw = {
  primaryText: "text-[#003B5C]",
  goldText: "text-[#E3A130]",
};

export default function Header({ darkMode, toggleDarkMode }) {
  const pathname = usePathname();
  const [openMenu, setOpenMenu] = useState(null);
  const [openFnbSubMenu, setOpenFnbSubMenu] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSubMenu, setMobileSubMenu] = useState(null);
  const [mobileFnbSubMenu, setMobileFnbSubMenu] = useState(null);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const handleCartUpdate = () => {
      try {
        const cart = JSON.parse(localStorage.getItem("cart") || "[]");
        if (Array.isArray(cart)) {
          const totalQty = cart.reduce(
            (sum, item) => sum + (item.quantity || 0),
            0
          );
          setCartCount(totalQty);
        } else {
          setCartCount(0);
        }
      } catch {
        setCartCount(0);
      }
    };

    if (typeof window !== "undefined") {
      handleCartUpdate();
      window.addEventListener("cartUpdated", handleCartUpdate);
      return () => window.removeEventListener("cartUpdated", handleCartUpdate);
    }
  }, []);

  // Fungsi untuk mengecek apakah menu aktif
  const isActive = (menu) => {
    switch (menu) {
      case "home":
        return pathname === "/" || pathname === "/home";
      case "business":
        return pathname.startsWith("/business");
      case "f&b/catering":
        return pathname.startsWith("/f&b/catering") || pathname.startsWith("/fandb/catering");
      case "f&b/cafetaria":
        return pathname.startsWith("/f&b/cafetaria") || pathname.startsWith("/fandb/cafetaria");
      case "other":
        return pathname.startsWith("/other");
      case "uiiishop":
        return pathname.startsWith("/merchandise");
      case "daycare":
        return pathname.startsWith("/daycare");
      case "mice":
        return pathname.startsWith("/mice");
      case "accommodation":
        return pathname.startsWith("/accommodation");
      case "news":
        return pathname.startsWith("/news");
      case "about":
        return pathname.startsWith("/about");
      case "report":
        return pathname.startsWith("/about/report");
      case "faq":
        return pathname.startsWith("/about/faq");
      case "donate":
        return pathname.startsWith("/endowment") || pathname.startsWith("/donate");
      case "cooperation":
        return pathname.startsWith("/cooperation");
      case "login":
        return pathname.startsWith("/auth/login");
      case "cart":
        return pathname.startsWith("/cart");
      default:
        return false;
    }
  };

  const isBusinessActive = isActive("business") || isActive("other") || 
                          isActive("uiiishop") || isActive("daycare") || isActive("mice") || 
                          isActive("accommodation") || isActive("f&b/catering") || isActive("f&b/cafetaria");
  const isAboutActive = isActive("about") || isActive("report") || isActive("faq");
  const isEndowmentActive = isActive("donate");

  return (
    <>
      {/* ================= TOP CONTACT BAR ================= */}
      <div className="w-full bg-[#003B5C] text-white text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-2">
          {/* Left */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-1">
              <svg
                width="16"
                height="16"
                fill="currentColor"
                className="inline-block"
              >
                <path d="M2 4.75A2.75 2.75 0 0 1 4.75 2h6.5A2.75 2.75 0 0 1 14 4.75v6.5A2.75 2.75 0 0 1 11.25 14h-6.5A2.75 2.75 0 0 1 2 11.25v-6.5ZM4.75 3.5A1.25 1.25 0 0 0 3.5 4.75v6.5c0 .69.56 1.25 1.25 1.25h6.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25h-6.5Zm.97 2.03a.75.75 0 1 1 1.06 1.06l-1.25 1.25 3.97 3.97 1.25-1.25a.75.75 0 1 1 1.06 1.06l-1.78 1.78a.75.75 0 0 1-1.06 0l-3.97-3.97a.75.75 0 0 1 0-1.06l1.78-1.78Z" />
              </svg>
              <span className="ml-1 hidden sm:inline">dpu@uiii.ac.id</span>
            </div>
            <a
              href="https://api.whatsapp.com/send/?phone=6281280006307&text=I'm+interested+in+buying+your+product&type=phone_number&app_absent=0"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:underline"
            >
              <MessageCircle size={14} />
              <span className="ml-1 hidden sm:inline">
                +62 812-8000-6307
              </span>
            </a>
            <Link href="/news" className="hover:underline">
              NEWS
            </Link>
            <Link href="/about/faq" className="hover:underline">
              FAQ
            </Link>
          </div>

          {/* Right */}
          <div className="flex items-center gap-2 sm:gap-4">
            <a
              href="https://instagram.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#E3A130] transition"
            >
              <Instagram size={18} />
            </a>
            <a
              href="https://twitter.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#E3A130] transition"
            >
              <Twitter size={18} />
            </a>
            <a
              href="https://facebook.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#E3A130] transition"
            >
              <Facebook size={18} />
            </a>

            {/* Cart Icon with Count - Desktop */}
            <Link
              href="/cart"
              className="relative cursor-pointer hidden md:block"
            >
              <ShoppingCart
                size={24}
                className="hover:text-[#003B5C] transition"
              />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-2 bg-red-500 text-white rounded-full text-xs w-5 h-5 flex items-center justify-center">
                  {cartCount > 9 ? "9+" : cartCount}
                </span>
              )}
            </Link>

            {/* Login button on desktop */}
            <Link
              href="/auth/login"
              className="ml-2 px-3 py-1 bg-[#E3A130] text-[#003B5C] rounded-full text-xs font-semibold hover:bg-[#ffd184] transition hidden md:inline-flex"
            >
              Login (Vendor)
            </Link>
          </div>
        </div>
      </div>

      {/* ================= MAIN HEADER ================= */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 dark:bg-dark-gray/70 border-b border-zinc-100 dark:border-dark-gray-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 focus:outline-none">
            <img
              src={logo?.src || logo}
              alt="UIII Business Development"
              className="h-8 sm:h-10 w-auto object-contain"
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-10">
            <nav className="flex items-center gap-6 text-sm">
              {/* HOME */}
              <Link
                href="/"
                className={`hover:text-zinc-500 ${
                  isActive("home") ? tw.goldText : ""
                }`}
              >
                HOME
              </Link>

              {/* ABOUT */}
              <div
                className="relative"
                onMouseEnter={() => setOpenMenu("about")}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <Link
                  href="/about"
                  className={`hover:text-zinc-500 ${
                    isAboutActive ? tw.goldText : ""
                  }`}
                >
                  ABOUT
                </Link>
              </div>

              {/* BUSINESS UNIT */}
              <div
                className="relative"
                onMouseEnter={() => setOpenMenu("business")}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <Link
                  href="/business"
                  className={`hover:text-zinc-500 ${
                    isBusinessActive ? tw.goldText : ""
                  }`}
                >
                  BUSINESS UNIT
                </Link>
                <div
                  className={`absolute left-0 top-full bg-white border border-zinc-200 rounded-md shadow-lg min-w-[200px] py-2 z-50 ${
                    openMenu === "business" ? "block" : "hidden"
                  }`}
                >
                  <Link
                    href="/merchandise"
                    className="block px-4 py-2 text-xs hover:bg-zinc-100"
                    onClick={() => setOpenMenu(null)}
                  >
                    Merchandise
                  </Link>
                  <Link
                    href="/mice"
                    className="block px-4 py-2 text-xs hover:bg-zinc-100"
                    onClick={() => setOpenMenu(null)}
                  >
                    MICE
                  </Link>
                  <Link
                    href="/accommodation"
                    className="block px-4 py-2 text-xs hover:bg-zinc-100"
                    onClick={() => setOpenMenu(null)}
                  >
                    Accommodation
                  </Link>
                  <Link
                    href="/daycare"
                    className="block px-4 py-2 text-xs hover:bg-zinc-100"
                    onClick={() => setOpenMenu(null)}
                  >
                    Daycare
                  </Link>
                  
                  {/* F&B as dropdown trigger without link - hanya sebagai label */}
                  <div 
                    className="relative"
                    onMouseEnter={() => setOpenFnbSubMenu(true)}
                    onMouseLeave={() => setOpenFnbSubMenu(false)}
                  >
                    <div
                      className="block px-4 py-2 text-xs text-zinc-700 flex justify-between items-center cursor-default"
                    >
                      <span>Food & Beverage</span>
                      <span className="ml-2 text-lg">›</span>
                    </div>
                    
                    {/* Nested dropdown - muncul di samping */}
                    {openFnbSubMenu && (
                      <div 
                        className="absolute left-full top-0 bg-white border border-zinc-200 rounded-md shadow-lg min-w-[150px] py-2 z-50"
                        onMouseEnter={() => setOpenFnbSubMenu(true)}
                        onMouseLeave={() => setOpenFnbSubMenu(false)}
                      >
                        <Link
                          href="/f&b/catering"
                          className={`block px-4 py-2 text-xs hover:bg-zinc-100 ${
                            isActive("f&b/catering") ? tw.goldText : ""
                          }`}
                          onClick={() => {
                            setOpenMenu(null);
                            setOpenFnbSubMenu(false);
                          }}
                        >
                          Catering
                        </Link>
                        <Link
                          href="/f&b/cafetaria"
                          className={`block px-4 py-2 text-xs hover:bg-zinc-100 ${
                            isActive("f&b/cafetaria") ? tw.goldText : ""
                          }`}
                          onClick={() => {
                            setOpenMenu(null);
                            setOpenFnbSubMenu(false);
                          }}
                        >
                          Cafetaria
                        </Link>
                      </div>
                    )}
                  </div>

                  <Link
                    href="/other"
                    className="block px-4 py-2 text-xs hover:bg-zinc-100"
                    onClick={() => setOpenMenu(null)}
                  >
                    Others
                  </Link>
                </div>
              </div>

              {/* COOPERATION */}
              <Link
                href="/cooperation"
                className={`hover:text-zinc-500 ${
                  isActive("cooperation") ? tw.goldText : ""
                }`}
              >
                COOPERATION
              </Link>

              {/* ENDOWMENT FUND */}
              <div
                className="relative"
                onMouseEnter={() => setOpenMenu("endowment")}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <a
                  href="https://old.uiii.ac.id/uiii-endowment-fund"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`hover:text-zinc-500 ${
                    isEndowmentActive ? tw.goldText : ""
                  }`}
                >
                  ENDOWMENT
                </a>
              </div>
            </nav>
          </div>

          {/* Mobile right side: cart + hamburger */}
          <div className="flex items-center gap-3 md:hidden">
            <Link href="/cart" className="relative" aria-label="Cart">
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-2 bg-red-500 text-white rounded-full text-[10px] w-4 h-4 flex items-center justify-center">
                  {cartCount > 9 ? "9+" : cartCount}
                </span>
              )}
            </Link>

            <button
              onClick={() => setMobileOpen((prev) => !prev)}
              aria-label="Toggle navigation"
            >
              {mobileOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* ================= MOBILE MENU ================= */}
        {mobileOpen && (
          <div className="md:hidden border-t border-zinc-200 bg-white">
            <nav className="max-w-7xl mx-auto px-4 py-3 space-y-1 text-sm">
              {/* HOME */}
              <Link
                href="/"
                className={`block w-full text-left py-2 px-2 ${
                  isActive("home") ? tw.goldText : "text-zinc-700"
                }`}
                onClick={() => setMobileOpen(false)}
              >
                HOME
              </Link>

              {/* ABOUT */}
              <div className="border-b border-zinc-100 pb-1">
                <div className="flex justify-between items-center">
                  <Link
                    href="/about"
                    className={`w-full text-left py-2 px-2 ${
                      isAboutActive ? tw.goldText : "text-zinc-700"
                    }`}
                    onClick={() => setMobileOpen(false)}
                  >
                    ABOUT
                  </Link>
                </div>
              </div>

              {/* BUSINESS UNIT */}
              <div className="border-b border-zinc-100 pb-1">
                <div className="flex justify-between items-center">
                  <Link
                    href="/business"
                    className={`w-full text-left py-2 px-2 ${
                      isBusinessActive ? tw.goldText : "text-zinc-700"
                    }`}
                    onClick={() => setMobileOpen(false)}
                  >
                    BUSINESS UNIT
                  </Link>
                  <span 
                    className="text-xs w-6 h-6 flex items-center justify-center hover:bg-zinc-100 rounded cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMobileSubMenu(mobileSubMenu === "business" ? null : "business");
                    }}
                  >
                    {mobileSubMenu === "business" ? "−" : "+"}
                  </span>
                </div>
                {mobileSubMenu === "business" && (
                  <div className="ml-4 space-y-1 border-l border-zinc-200 pl-3 mt-1">
                    <Link
                      href="/merchandise"
                      className={`block w-full text-left py-1 ${
                        isActive("uiiishop") ? tw.goldText : "text-zinc-700"
                      }`}
                      onClick={() => setMobileOpen(false)}
                    >
                      Merchandise
                    </Link>
                    <Link
                      href="/mice"
                      className={`block w-full text-left py-1 ${
                        isActive("mice") ? tw.goldText : "text-zinc-700"
                      }`}
                      onClick={() => setMobileOpen(false)}
                    >
                      MICE
                    </Link>
                    <Link
                      href="/accommodation"
                      className={`block w-full text-left py-1 ${
                        isActive("accommodation") ? tw.goldText : "text-zinc-700"
                      }`}
                      onClick={() => setMobileOpen(false)}
                    >
                      Accommodation
                    </Link>
                    <Link
                      href="/daycare"
                      className={`block w-full text-left py-1 ${
                        isActive("daycare") ? tw.goldText : "text-zinc-700"
                      }`}
                      onClick={() => setMobileOpen(false)}
                    >
                      Daycare
                    </Link>
                    
                    {/* F&B as label without link for mobile */}
                    <div>
                      <div className="flex justify-between items-center">
                        <span className="block w-full text-left py-1 text-zinc-700 cursor-default">
                          Food & Beverage
                        </span>
                        <span 
                          className="text-xs w-6 h-6 flex items-center justify-center hover:bg-zinc-100 rounded cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMobileFnbSubMenu(mobileFnbSubMenu === "fnb" ? null : "fnb");
                          }}
                        >
                          {mobileFnbSubMenu === "fnb" ? "−" : "+"}
                        </span>
                      </div>
                      {mobileFnbSubMenu === "fnb" && (
                        <div className="ml-4 space-y-1 border-l border-zinc-200 pl-3 mt-1">
                          <Link
                            href="/f&b/catering"
                            className={`block w-full text-left py-1 ${
                              isActive("f&b/catering") ? tw.goldText : "text-zinc-700"
                            }`}
                            onClick={() => setMobileOpen(false)}
                          >
                            Catering
                          </Link>
                          <Link
                            href="/f&b/cafetaria"
                            className={`block w-full text-left py-1 ${
                              isActive("f&b/cafetaria") ? tw.goldText : "text-zinc-700"
                            }`}
                            onClick={() => setMobileOpen(false)}
                          >
                            Cafetaria
                          </Link>
                        </div>
                      )}
                    </div>

                    <Link
                      href="/other"
                      className={`block w-full text-left py-1 ${
                        isActive("other") ? tw.goldText : "text-zinc-700"
                      }`}
                      onClick={() => setMobileOpen(false)}
                    >
                      Others
                    </Link>
                  </div>
                )}
              </div>

              {/* COOPERATION */}
              <Link
                href="/cooperation"
                className={`block w-full text-left py-2 px-2 ${
                  isActive("cooperation") ? tw.goldText : "text-zinc-700"
                }`}
                onClick={() => setMobileOpen(false)}
              >
                COOPERATION
              </Link>

              {/* ENDOWMENT FUND */}
              <a
                href="https://old.uiii.ac.id/uiii-endowment-fund"
                target="_blank"
                rel="noopener noreferrer"
                className={`block w-full text-left py-2 px-2 ${
                  isEndowmentActive ? tw.goldText : "text-zinc-700"
                }`}
                onClick={() => setMobileOpen(false)}
              >
                ENDOWMENT
              </a>

              {/* LOGIN */}
              <Link
                href="/auth/login"
                className={`block w-full text-left py-2 px-2 ${
                  isActive("login") ? tw.goldText : "text-zinc-700"
                }`}
                onClick={() => setMobileOpen(false)}
              >
                Login (Vendor)
              </Link>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}